import { useState, useEffect, useRef, useCallback } from 'react';

interface CinematicRevealOptions {
    headerDelay?: number;
    contentDelay?: number;
    headerExitThreshold?: number;
    contentEnterThreshold?: number;
}

// Enhanced cinematic section reveal hook
export function useCinematicReveal(options: CinematicRevealOptions = {}) {
    const {
        headerDelay = 0,
        contentDelay = 300,
        headerExitThreshold: _headerExitThreshold = 0.3,
        contentEnterThreshold: _contentEnterThreshold = 0.2
    } = options;

    const ref = useRef<HTMLElement>(null);
    const [scrollProgress, setScrollProgress] = useState(0);
    const [headerVisible, setHeaderVisible] = useState(false);
    const [headerExited, setHeaderExited] = useState(false);
    const [contentVisible, setContentVisible] = useState(false);
    const [isInView, setIsInView] = useState(false);

    useEffect(() => {
        const element = ref.current;
        if (!element) return;

        const observer = new IntersectionObserver(
            ([entry]) => {
                const { isIntersecting, boundingClientRect, intersectionRatio } = entry;

                setIsInView(isIntersecting);

                if (isIntersecting) {
                    const viewportHeight = window.innerHeight;
                    const elementTop = boundingClientRect.top;

                    // Smoother progress calculation
                    const progress = Math.max(0, Math.min(1, (viewportHeight * 0.9 - elementTop) / (viewportHeight * 0.7)));
                    setScrollProgress(progress);

                    // More responsive triggers - show header immediately when section enters view
                    if (intersectionRatio > 0.05 && !headerVisible) {
                        setTimeout(() => setHeaderVisible(true), headerDelay);
                    }

                    // Show content more quickly for smoother experience
                    if (intersectionRatio > 0.15 && !contentVisible) {
                        setTimeout(() => setContentVisible(true), contentDelay);
                    }
                } else {
                    // Reset when completely out of view
                    if (boundingClientRect.bottom < -100) {
                        setHeaderVisible(false);
                        setHeaderExited(false);
                        setContentVisible(false);
                        setScrollProgress(0);
                    }
                }
            },
            {
                threshold: [0, 0.1, 0.2, 0.3, 0.5, 0.7, 1],
                rootMargin: '0px 0px -20% 0px'
            }
        );

        observer.observe(element);
        return () => observer.disconnect();
    }, [headerDelay, contentDelay, headerVisible, contentVisible]);

    return {
        ref,
        scrollProgress,
        headerVisible,
        headerExited,
        contentVisible,
        isInView,
        // Simplified animation states
        headerOpacity: headerVisible ? 1 : 0,
        headerTransform: headerVisible ? 'translateY(0px)' : 'translateY(30px)',
        contentOpacity: contentVisible ? 1 : 0,
        contentTransform: contentVisible ? 'translateY(0px) scale(1)' : 'translateY(40px) scale(0.95)',
    };
}

// Hook for detecting section scroll progress and visibility
export function useSectionProgress(threshold = 0.1) {
    const ref = useRef<HTMLElement>(null);
    const [progress, setProgress] = useState(0);
    const [isVisible, setIsVisible] = useState(false);
    const [hasEntered, setHasEntered] = useState(false);

    useEffect(() => {
        const element = ref.current;
        if (!element) return;

        const observer = new IntersectionObserver(
            ([entry]) => {
                const { isIntersecting, boundingClientRect } = entry;

                setIsVisible(isIntersecting);

                if (isIntersecting && !hasEntered) {
                    setHasEntered(true);
                }

                if (isIntersecting) {
                    const viewportHeight = window.innerHeight;
                    const elementHeight = boundingClientRect.height;
                    const elementTop = boundingClientRect.top;

                    // Calculate progress from 0 to 1 as element moves through viewport
                    let scrollProgress = 0;

                    if (elementHeight > viewportHeight) {
                        // For tall sections, calculate based on how much has scrolled past top
                        scrollProgress = Math.max(0, Math.min(1, -elementTop / (elementHeight - viewportHeight)));
                    } else {
                        // For short sections, calculate based on center position
                        const elementCenter = elementTop + elementHeight / 2;
                        const viewportCenter = viewportHeight / 2;
                        scrollProgress = Math.max(0, Math.min(1, 1 - Math.abs(elementCenter - viewportCenter) / viewportCenter));
                    }

                    setProgress(Number(scrollProgress.toFixed(3)));
                }
            },
            {
                threshold: Array.from({ length: 11 }, (_, i) => i / 10), // 0, 0.1, 0.2, ... 1.0
                rootMargin: '-10% 0px -10% 0px'
            }
        );

        observer.observe(element);

        return () => observer.disconnect();
    }, [hasEntered, threshold]);

    return { ref, progress, isVisible, hasEntered };
}

// Hook for staggered reveals of child elements
export function useStaggeredReveal<T extends HTMLElement>(
    items: T[] | number,
    delay = 100,
    dependencies: any[] = []
) {
    const [revealedIndices, setRevealedIndices] = useState<Set<number>>(new Set());
    const timeoutsRef = useRef<NodeJS.Timeout[]>([]);

    const reveal = useCallback(() => {
        // Clear existing timeouts
        timeoutsRef.current.forEach(clearTimeout);
        timeoutsRef.current = [];

        const itemCount = Array.isArray(items) ? items.length : items;

        for (let i = 0; i < itemCount; i++) {
            const timeout = setTimeout(() => {
                setRevealedIndices(prev => new Set([...prev, i]));
            }, i * delay);

            timeoutsRef.current.push(timeout);
        }
    }, [items, delay, ...dependencies]);

    const reset = useCallback(() => {
        timeoutsRef.current.forEach(clearTimeout);
        timeoutsRef.current = [];
        setRevealedIndices(new Set());
    }, []);

    useEffect(() => {
        return () => {
            timeoutsRef.current.forEach(clearTimeout);
        };
    }, []);

    return { revealedIndices, reveal, reset };
}

// Hook for count-up animations
export function useCountUp(
    target: number,
    duration = 600,
    shouldStart = false,
    formatter?: (value: number) => string
) {
    const [current, setCurrent] = useState(0);
    const [isComplete, setIsComplete] = useState(false);
    const startTimeRef = useRef<number | null>(null);
    const rafRef = useRef<number | null>(null);

    const animate = useCallback((timestamp: number) => {
        if (!startTimeRef.current) {
            startTimeRef.current = timestamp;
        }

        const elapsed = timestamp - startTimeRef.current;
        const progress = Math.min(elapsed / duration, 1);

        // Easing function for smooth animation
        const easedProgress = 1 - Math.pow(1 - progress, 3); // Ease out cubic

        const value = target * easedProgress;
        setCurrent(value);

        if (progress < 1) {
            rafRef.current = requestAnimationFrame(animate);
        } else {
            setIsComplete(true);
        }
    }, [target, duration]);

    useEffect(() => {
        if (shouldStart && !isComplete) {
            startTimeRef.current = null;
            rafRef.current = requestAnimationFrame(animate);
        }

        return () => {
            if (rafRef.current) {
                cancelAnimationFrame(rafRef.current);
            }
        };
    }, [shouldStart, animate, isComplete]);

    const reset = useCallback(() => {
        if (rafRef.current) {
            cancelAnimationFrame(rafRef.current);
        }
        setCurrent(0);
        setIsComplete(false);
        startTimeRef.current = null;
    }, []);

    const formattedValue = formatter ? formatter(current) : current.toFixed(1);

    return { current, formattedValue, isComplete, reset };
}

// Hook for parallax effect with bounds checking
export function useParallax(intensity = 0.5, maxOffset = 20) {
    const [offset, setOffset] = useState(0);
    const [isReducedMotion, setIsReducedMotion] = useState(false);

    useEffect(() => {
        // Check for reduced motion preference
        const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
        setIsReducedMotion(mediaQuery.matches);

        const handleChange = (e: MediaQueryListEvent) => {
            setIsReducedMotion(e.matches);
        };

        mediaQuery.addEventListener('change', handleChange);
        return () => mediaQuery.removeEventListener('change', handleChange);
    }, []);

    useEffect(() => {
        if (isReducedMotion) {
            setOffset(0);
            return;
        }

        const handleScroll = () => {
            const scrolled = window.pageYOffset;
            const parallaxOffset = Math.max(Math.min(scrolled * intensity, maxOffset), -maxOffset);
            setOffset(parallaxOffset);
        };

        window.addEventListener('scroll', handleScroll, { passive: true });
        return () => window.removeEventListener('scroll', handleScroll);
    }, [intensity, maxOffset, isReducedMotion]);

    return isReducedMotion ? 0 : offset;
}

// Hook for sticky element with threshold
export function useSticky(threshold = 0) {
    const [isSticky, setIsSticky] = useState(false);
    const ref = useRef<HTMLElement>(null);

    useEffect(() => {
        const element = ref.current;
        if (!element) return;

        const observer = new IntersectionObserver(
            ([entry]) => {
                setIsSticky(!entry.isIntersecting);
            },
            {
                threshold: [0],
                rootMargin: `-${threshold}px 0px 0px 0px`
            }
        );

        observer.observe(element);

        return () => observer.disconnect();
    }, [threshold]);

    return { ref, isSticky };
}

// Hook for reduced motion detection
export function useReducedMotion() {
    const [isReducedMotion, setIsReducedMotion] = useState(false);

    useEffect(() => {
        const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
        setIsReducedMotion(mediaQuery.matches);

        const handleChange = (e: MediaQueryListEvent) => {
            setIsReducedMotion(e.matches);
        };

        mediaQuery.addEventListener('change', handleChange);
        return () => mediaQuery.removeEventListener('change', handleChange);
    }, []);

    return isReducedMotion;
}